import React, { useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, AlertTriangle, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { api } from "../lib/api";
import { celebrateAchievement } from "../lib/achievementUX";

export const ISSUE_TYPES = [
  { value: "path_blocked", label: "Path Blocked / Obstruction" },
  { value: "new_fence_stile", label: "New Fence / Stile" },
  { value: "dangerous_terrain", label: "Dangerous Terrain" },
  { value: "inaccurate_map", label: "Inaccurate Map" },
  { value: "other", label: "Other" },
];

// Must mirror SUB_CATEGORIES in backend/routes/trail_reports.py
export const SUB_CATEGORIES = {
  new_fence_stile: [
    { value: "high_stile_no_gate", label: "High Stile (No Dog Gate)" },
    { value: "electric_fence", label: "Electric Fence" },
  ],
  dangerous_terrain: [
    { value: "blue_green_algae", label: "Blue-Green Algae" },
    { value: "livestock_present", label: "Livestock Present" },
    { value: "broken_glass", label: "Broken Glass" },
  ],
  path_blocked: [
    { value: "overgrown", label: "Overgrown / Thorns" },
    { value: "flooded", label: "Flooded / Boggy" },
    { value: "fallen_tree", label: "Fallen Tree" },
  ],
  inaccurate_map: [
    { value: "trailhead_moved", label: "Trailhead Moved" },
    { value: "transit_changed", label: "Transit Link Changed" },
  ],
  other: [],
};

export function labelForSubCategory(issueType, value) {
  return (SUB_CATEGORIES[issueType] || []).find((s) => s.value === value)?.label || value || "";
}

const MAX_BYTES = 35 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
// Shared with PhotoGallery — long-edge 1920 keeps the report image sharp on
// the admin dashboard without pushing 12 MP RAW files down 4G.
const COMPRESSION_OPTS = {
  maxSizeMB: 1.5,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.82,
};

export default function ReportIssueModal({ trailId, trailName, onClose, onSubmitted }) {
  const navigate = useNavigate();
  const [issueType, setIssueType] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const subs = useMemo(() => SUB_CATEGORIES[issueType] || [], [issueType]);

  const onIssueTypeChange = (val) => {
    setIssueType(val);
    setSubCategory("");
  };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError("Photo must be JPEG, PNG or WEBP.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("Photo is too large (max 35 MB).");
      return;
    }
    setError("");

    // Compress in-browser so a 12 MP camera photo doesn't cost the walker
    // a MB of upload on their next 4G shift report.
    setCompressing(true);
    let toStore = f;
    try {
      const out = await imageCompression(f, COMPRESSION_OPTS);
      toStore = new File([out], f.name, { type: out.type || f.type });
    } catch {
      // Fall back to the original; the backend still enforces 35 MB.
    } finally {
      setCompressing(false);
    }
    setFile(toStore);
    const r = new FileReader();
    r.onload = () => setPreview(r.result);
    r.readAsDataURL(toStore);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (!issueType) {
      setError("Please pick an issue type before submitting.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("issue_type", issueType);
      if (subCategory) fd.append("sub_category", subCategory);
      fd.append("description", description);
      if (file) fd.append("image", file);
      const res = await api.post(`/trails/${trailId}/report`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Report submitted — thanks for keeping the trail safe");
      // Trophy toast — success payload includes `new_achievement: "messenger"`
      // on the walker's first report. Silent no-op otherwise.
      celebrateAchievement(navigate, res?.data?.new_achievement);
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send the report — try again";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop — separate fixed layer so clicks outside still close, while
          the modal owns its own viewport anchoring (same pattern as
          ScoutRatingModal) so mobile browser chrome can't hide the footer. */}
      <div
        className="fixed inset-0 overflow-hidden z-[1100] bg-brand-text/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="report-issue-backdrop"
      />
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="report-issue-modal"
        // Mobile: pinned into safe viewport space with explicit top/bottom
        // insets so the buttons never land under the OS chrome or app
        // bottom-nav. Desktop (md+): reverts to a centred card.
        className="fixed inset-x-4 top-2 bottom-12 z-[1101] flex flex-col overflow-hidden md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:h-auto md:max-h-[85vh] bg-brand-surface rounded-3xl shadow-2xl border border-brand-border/60 animate-fade-up"
      >
        {/* Header — fixed at top */}
        <div className="p-6 pb-3 border-b border-brand-border/50 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
              <AlertTriangle size={18} className="text-brand-accent" />
              Report an issue
            </h3>
            <button onClick={onClose} className="text-brand-subtle" aria-label="Close" data-testid="report-close">
              <X size={20} />
            </button>
          </div>
          <p className="text-xs text-brand-subtle truncate">{trailName}</p>
        </div>

        {/* Scrolling middle — min-h-0 so the flex column can actually shrink */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 pt-4" data-testid="report-issue-form">

        <label className="block text-sm font-medium text-brand-text">
          Issue type <span className="text-brand-error">*</span>
        </label>
        <select
          data-testid="report-issue-type"
          value={issueType}
          onChange={(e) => onIssueTypeChange(e.target.value)}
          className="td-input mt-1"
        >
          <option value="">— Choose an issue —</option>
          {ISSUE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        {/* Conditional secondary picker */}
        {subs.length > 0 && (
          <div data-testid="report-sub-category-wrapper" className="animate-fade-up">
            <label className="block text-sm font-medium text-brand-text mt-4">
              Specifically
            </label>
            <select
              data-testid="report-sub-category"
              value={subCategory}
              onChange={(e) => setSubCategory(e.target.value)}
              className="td-input mt-1"
            >
              <option value="">— Optional, but helps us triage —</option>
              {subs.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        )}

        <label className="block text-sm font-medium text-brand-text mt-4">Description</label>
        <textarea
          data-testid="report-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Where exactly is the issue? Any landmarks or context?"
          maxLength={2000}
          className="td-input mt-1 resize-none"
        />

        <label className="block text-sm font-medium text-brand-text mt-4">Photo (optional)</label>
        <div className="mt-1">
          {preview ? (
            <div className="relative rounded-2xl overflow-hidden border border-brand-border">
              <img src={preview} alt="" className="w-full max-h-56 object-cover" />
              <button
                data-testid="report-photo-clear"
                type="button"
                onClick={clearFile}
                className="absolute top-2 right-2 bg-brand-text/70 text-brand-sand rounded-full p-1.5"
                aria-label="Remove photo"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ) : (
            <label
              htmlFor="report-photo-input"
              className={`flex items-center justify-center gap-2 border-2 border-dashed border-brand-border rounded-2xl py-6 text-sm text-brand-subtle cursor-pointer hover:bg-brand-sand transition-colors ${compressing ? "opacity-70 pointer-events-none" : ""}`}
              data-testid="report-photo-label"
            >
              {compressing ? (
                <><Loader2 size={16} className="animate-spin" /> Compressing…</>
              ) : (
                <><ImagePlus size={18} /> Tap to add a photo (JPEG/PNG/WEBP, max 35 MB)</>
              )}
            </label>
          )}
          <input
            ref={inputRef}
            id="report-photo-input"
            data-testid="report-photo-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={pickFile}
            className="hidden"
          />
        </div>

        </div>

        {/* Footer — shrink-0 so the actions stay permanently docked and
            visible above the mobile bottom-nav / safari toolbar. */}
        <div
          className="shrink-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-brand-surface border-t border-brand-border/50"
          data-testid="report-issue-footer"
        >
          {error && (
            <div data-testid="report-error" className="mb-2 text-sm text-brand-error">
              {error}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              data-testid="report-cancel"
              type="button"
              onClick={onClose}
              className="td-btn-secondary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              data-testid="report-submit"
              type="button"
              onClick={submit}
              disabled={submitting || compressing}
              className="td-btn-primary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              Submit report
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
